import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, FileSearch, Network, Layers, MapPinned, ArrowRight, Check, FileCheck, Ruler, Activity, BarChart3, Gavel, Footprints, Lock, Clock, Handshake, ScrollText, Radar, CheckCircle2, Hammer, LogOut, Database, MessageSquare } from "lucide-react";
import { Reveal, ASSETS } from "@/lib/motion";
import Nav from "@/components/bwg/Nav";
import Footer from "@/components/bwg/Footer";
import WhatsAppFab from "@/components/bwg/WhatsAppFab";
import FaqSection, { FAQS } from "@/components/bwg/FaqSection";
import Seo from "@/components/bwg/Seo";
import { useBooking } from "@/components/bwg/BookingModal";
import { orgSchema, faqSchema, breadcrumbSchema } from "@/lib/schema";

const GROUND = [
  { icon: Users, n: "01", title: "Village Level", body: "Direct conversations with landowners, brokers and village contacts. This is the first, unfiltered read on what a parcel really is." },
  { icon: FileSearch, n: "02", title: "Tehsil Records", body: "Official records are pulled to verify ownership, survey details and legal history against what the ground tells us." },
  { icon: Network, n: "03", title: "Local Network", body: "Our registry-office and stakeholder network validates the parcel before anyone else in the market has seen it." },
];

const CHECKLIST = [
  { icon: Gavel, title: "Tender Study", body: "Every notified government tender and infrastructure project within radius is mapped." },
  { icon: BarChart3, title: "Demand-Supply Intelligence", body: "Checked against comparable registered transactions in the micro-market." },
  { icon: Activity, title: "Registry Velocity", body: "Transaction pace measured against the corridor average to read real momentum." },
  { icon: Footprints, title: "On-ground Liaisoning", body: "Farmer-level title and boundary walked in person before a parcel qualifies." },
  { icon: FileCheck, title: "CLU Status", body: "Verified against the latest government notification, never assumed from precedent." },
  { icon: Ruler, title: "FAR Entitlement", body: "Floor Area Ratio verified against current policy, not a prior cycle's numbers." },
];

// Demand-Supply Intelligence: the two halves of the method.
const DUAL = [
  { icon: Footprints, tag: "Offline", title: "On-ground field data", body: "Direct farmer and landowner conversations, physical boundary checks, and a registry-office network that reads a micro-market before it shows up in any database." },
  { icon: Database, tag: "Online", title: "Registry & government data", body: "Sale-deed registrations, CLU notifications, sanctioned tenders and infrastructure approvals, pulled from public records and read together as one picture." },
];

// How BWG actually works with an investor's capital.
const ENGAGE = [
  { icon: MessageSquare, n: "01", title: "A conversation, not a pitch", body: "We understand what you want from land and answer every question. No pooled fund, no pressure." },
  { icon: FileSearch, n: "02", title: "We source and diligence a parcel", body: "A specific parcel is identified and cleared through our full verification process, then presented to you as a Site Intelligence Brief." },
  { icon: ScrollText, n: "03", title: "You take registered title", body: "You acquire the parcel in your own name. The land is yours, held directly, never through a pooled vehicle." },
  { icon: Handshake, n: "04", title: "We manage through to exit", body: "We position the asset and handle the exit. We do not charge on the way in. Our compensation is earned at exit, tied to your profit." },
];

// Title & encroachment risk controls.
const RISK = [
  { icon: ScrollText, title: "Title & documentation", body: "Ownership, encumbrance history and survey details are cross-checked across tehsil and mutation records, then independently validated by Uttar Pradesh-specialist legal partners before a rupee moves. This is the CLU Status and records work in our six-point clearance, applied to every parcel." },
  { icon: Lock, title: "Kabza & encroachment", body: "Possession is physically walked and confirmed on the ground, boundaries verified against the record, and any occupation or dispute surfaced through our liaisoning network. This is the On-ground Liaisoning step of the clearance. A parcel with unclear possession never reaches an investor." },
];

// Reuse of the already-verified Gata-1184 case as an illustrative walkthrough.
const CASE_STEPS = [
  { icon: Radar, title: "Identified early", body: "The parcel surfaced through infrastructure and registry signals before the wider market repriced it." },
  { icon: CheckCircle2, title: "Cleared and validated", body: "Legal path, land use and registry-backed demand were confirmed through the six-point process." },
  { icon: Hammer, title: "Positioned", body: "Structured and positioned for its highest-conviction exit." },
  { icon: LogOut, title: "Exited", body: "Realized a 3.4x return. A confirmed historical result, not a forward projection." },
];

function InteriorHero({ img, eyebrow, title, sub }) {
  return (
    <header className="relative min-h-[70vh] flex items-end overflow-hidden bg-brand-dark">
      <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/60 to-brand-dark/40" />
      <div className="grain" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pb-16 pt-32 w-full">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-3 mb-6">
          <span className="h-px w-10 bg-brand-orange" />
          <span className="text-brand-offwhite/70 text-xs tracking-[0.25em] uppercase font-medium">{eyebrow}</span>
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-display font-semibold text-brand-offwhite text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[0.98] max-w-4xl">
          {title}
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.8 }}
          className="mt-6 max-w-2xl text-lg text-brand-offwhite/75 leading-relaxed">{sub}</motion.p>
      </div>
    </header>
  );
}

export default function OurApproach() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const { open: openBooking } = useBooking();
  return (
    <main className="bg-brand-paper overflow-x-hidden">
      <Seo
        title="Our Approach: How BWG Finds Land Before the Market | FAR & CLU Explained"
        description="How Bhaarat Wealth Group finds land before the market: ground verification, a six-point clearance checklist, and how Floor Area Ratio (FAR) and Change of Land Use (CLU) drive land value in India."
        path="/our-approach"
        schema={[orgSchema(), faqSchema(FAQS), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Our Approach", path: "/our-approach" }])]}
      />
      <Nav overHero />
      <InteriorHero img={ASSETS.groundVerify} eyebrow="Our Working"
        title="How we find land before the market does."
        sub="Most participants price land on what it is today. We price it on what it is permitted to become." />

      {/* Origin story */}
      <section className="bg-brand-offwhite py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">The Origin</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05]">The problem was never the land. It was the record.</h2>
            <p className="mt-6 text-lg text-brand-grey leading-relaxed">
              Land records in India are fragmented across offices such as the tehsil, mutation records and layout approvals, and no single source tells the whole story.
              A parcel can look clean in one register and carry a boundary dispute in another. That gap, between what a record says and what the ground shows, is exactly why Bhaarat Wealth Group exists.
            </p>
            <p className="mt-5 text-lg text-brand-grey leading-relaxed">
              So we built a method around it, not a hunch. We call it demand-supply intelligence: a repeatable way of combining what the ground tells us with what the records prove, so a decision rests on evidence rather than guesswork.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Demand-Supply Intelligence: the dual method */}
      <section className="bg-brand-paper py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">The Method</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05] max-w-3xl">Two sources of truth, read as one.</h2>
            <p className="mt-6 text-lg text-brand-grey leading-relaxed max-w-2xl">Most people rely on one or the other. We insist on both. When on-ground reality and the official record agree, an outcome becomes far more predictable than a guess ever could be.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 mt-14">
            {DUAL.map((d, i) => (
              <Reveal key={d.title} delay={i * 0.12}>
                <div className="h-full bg-brand-card border border-brand-ink/10 rounded-sm p-8 hover:border-brand-orange transition-colors duration-500">
                  <div className="flex items-center justify-between mb-5">
                    <span className="w-12 h-12 rounded-sm bg-brand-orange/10 flex items-center justify-center">
                      <d.icon className="w-6 h-6 text-brand-orange" strokeWidth={1.5} />
                    </span>
                    <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-brand-orange">{d.tag}</span>
                  </div>
                  <h3 className="font-display font-semibold text-2xl tracking-tight text-brand-ink">{d.title}</h3>
                  <p className="mt-3 text-brand-grey leading-relaxed">{d.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ground verification */}
      <section className="bg-brand-paper py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Ground Verification</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05] max-w-2xl">We verify from the ground up, in that order.</h2>
          </Reveal>
          <div className="relative mt-16">
            <div className="hidden md:block absolute top-10 left-[16%] right-[16%] h-px bg-gradient-to-r from-brand-orange/20 via-brand-orange/60 to-brand-orange/20" />
            <div className="grid md:grid-cols-3 gap-12 md:gap-6">
              {GROUND.map((g, i) => (
                <Reveal key={g.n} delay={i * 0.12}>
                  <div className="relative flex flex-col items-center text-center px-2">
                    <div className="relative z-10 w-20 h-20 rounded-full bg-brand-paper border-2 border-brand-orange flex items-center justify-center">
                      <g.icon className="w-8 h-8 text-brand-orange" strokeWidth={1.5} />
                      <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-brand-orange text-white text-xs font-semibold flex items-center justify-center">{g.n}</span>
                    </div>
                    <h3 className="mt-6 font-display font-semibold text-2xl tracking-tight text-brand-ink">{g.title}</h3>
                    <p className="mt-3 text-brand-grey leading-relaxed text-[15px] max-w-xs">{g.body}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Standardising + Farmer level */}
      <section className="bg-brand-dark py-24 lg:py-32 relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-14">
            <Reveal>
              <Layers className="w-9 h-9 text-brand-orange mb-6" strokeWidth={1.5} />
              <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tighter text-brand-offwhite leading-tight">Standardising land into signal.</h2>
              <p className="mt-5 text-brand-offwhite/65 leading-relaxed">
                Raw ground data is inconsistent by nature. Different villages and registrars record the same facts differently. We convert that noise into signal by applying one fixed assessment framework to every parcel, regardless of where or how it was sourced.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <MapPinned className="w-9 h-9 text-brand-orange mb-6" strokeWidth={1.5} />
              <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tighter text-brand-offwhite leading-tight">The real profit sits at the farmer level.</h2>
              <p className="mt-5 text-brand-offwhite/65 leading-relaxed">
                Land closest to the source is the least standardised: mismatched names across deeds, memory-drawn boundaries, and title passing through inheritance before the paperwork catches up. That is exactly where we work.
              </p>
            </Reveal>
          </div>
          <Reveal>
            <div className="mt-16 border-t border-brand-offwhite/10 pt-10 grid sm:grid-cols-3 gap-8">
              <div>
                <p className="font-display font-semibold text-4xl lg:text-5xl tracking-tighter text-brand-orange">1</p>
                <p className="mt-2 text-brand-offwhite/60 text-sm max-w-[16rem]">assessment framework applied to every parcel, whatever its source</p>
              </div>
              <div>
                <p className="font-display font-semibold text-4xl lg:text-5xl tracking-tighter text-brand-orange">Farmer-level</p>
                <p className="mt-2 text-brand-offwhite/60 text-sm max-w-[16rem]">one of the largest direct relationship networks in this space</p>
              </div>
              <div>
                <p className="font-display font-semibold text-4xl lg:text-5xl tracking-tighter text-brand-orange">Source-first</p>
                <p className="mt-2 text-brand-offwhite/60 text-sm max-w-[16rem]">we buy where land is least standardised, and least contested on price</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Six-point checklist */}
      <section className="bg-brand-offwhite py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">The Clearance</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05] max-w-2xl">Six points to clear before any parcel reaches an investor.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-14">
            {CHECKLIST.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div className="group h-full bg-brand-card border border-brand-ink/10 rounded-sm p-7 hover:border-brand-orange transition-colors duration-500">
                  <div className="flex items-center justify-between mb-5">
                    <span className="w-12 h-12 rounded-sm bg-brand-orange/10 flex items-center justify-center">
                      <c.icon className="w-6 h-6 text-brand-orange" strokeWidth={1.5} />
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-orange">
                      <Check className="w-3.5 h-3.5" /> Cleared
                    </span>
                  </div>
                  <h3 className="font-display font-semibold text-xl tracking-tight text-brand-ink">{c.title}</h3>
                  <p className="mt-2 text-brand-grey leading-relaxed text-[15px]">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Title & encroachment risk */}
      <section className="bg-brand-dark py-24 lg:py-32 relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">The Certainty</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-offwhite leading-[1.05] max-w-3xl">Your two biggest fears, taken off the table.</h2>
            <p className="mt-6 text-brand-offwhite/65 leading-relaxed max-w-2xl">Unclear paperwork and illegal occupation, or kabza, are what keep people out of land. Both are handled inside the same six-point clearance every parcel already passes, before it is ever offered to you.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6 mt-14">
            {RISK.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.12}>
                <div className="h-full bg-brand-offwhite/[0.04] border border-brand-offwhite/10 rounded-sm p-8">
                  <r.icon className="w-9 h-9 text-brand-orange mb-6" strokeWidth={1.5} />
                  <h3 className="font-display font-semibold text-2xl text-brand-offwhite">{r.title}</h3>
                  <p className="mt-3 text-brand-offwhite/65 leading-relaxed">{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAR & CLU explainer (SEO) */}
      <section className="bg-brand-paper py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">The Multipliers</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05]">FAR and CLU aren't indicators. They're multipliers.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-10 mt-12">
            <Reveal>
              <div className="border-l-2 border-brand-orange pl-6">
                <h3 className="font-display font-semibold text-2xl text-brand-ink">What is Floor Area Ratio (FAR)?</h3>
                <p className="mt-3 text-brand-grey leading-relaxed">
                  Floor Area Ratio is the ratio of a building's total constructible floor area to the size of the plot it sits on.
                  In Indian real estate, FAR is set by the local master plan and directly caps how much can be built, so a higher FAR entitlement can multiply the developable value of the same piece of land.
                  We verify FAR against current policy for every parcel, because entitlements change between planning cycles.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="border-l-2 border-brand-orange pl-6">
                <h3 className="font-display font-semibold text-2xl text-brand-ink">What is Change of Land Use (CLU)?</h3>
                <p className="mt-3 text-brand-grey leading-relaxed">
                  Change of Land Use is the legal conversion of a parcel's permitted use, for example from agricultural to commercial or residential.
                  CLU is what turns cheap agricultural land into developable, re-ratable land. We verify CLU status against the latest notification and never assume it from a neighbouring parcel's precedent.
                </p>
              </div>
            </Reveal>
          </div>

          {/* Time-bound opportunity zones (extends the FAR/CLU re-rating idea) */}
          <Reveal delay={0.1}>
            <div className="mt-14 bg-brand-card border border-brand-ink/10 rounded-sm p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-6 h-6 text-brand-orange" strokeWidth={1.5} />
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-orange">Time-bound opportunity zones</span>
              </div>
              <p className="text-lg text-brand-grey leading-relaxed">
                A FAR or CLU re-rating rarely waits for a road to open. Government infrastructure runs on published timelines, so tenders, sanctions and completion dates give a parcel a window: the closer a corridor moves to becoming inevitable, the sooner permitted use and price begin to re-rate.
                Reading those timelines is how we tell which parcels re-rate ahead of completion, and when that window is likely to open.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Case walkthrough: reuse of the verified Gata-1184 result */}
      <section className="bg-brand-offwhite py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
            <div className="lg:col-span-8">
              <Reveal>
                <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">A Deal, Start to Finish</span>
                <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05]">How we picked one parcel, and the process we followed.</h2>
                <p className="mt-6 text-lg text-brand-grey leading-relaxed max-w-2xl">Gata-1184 in Ayodhya is the clearest illustration of the method end to end. Every step below is the same clearance and intelligence work described on this page, applied to a single, real parcel.</p>
              </Reveal>
            </div>
            <div className="lg:col-span-4">
              <Reveal delay={0.12}>
                <div className="bg-brand-dark rounded-sm p-8 border border-brand-orange/40">
                  <p className="text-xs tracking-[0.2em] uppercase text-brand-offwhite/50">Realized result</p>
                  <div className="mt-2 font-display font-semibold text-6xl tracking-tighter text-brand-orange">3.4x</div>
                  <p className="mt-2 text-brand-offwhite/70 text-sm">Gata-1184, exited. A confirmed historical result, not a forward projection.</p>
                </div>
              </Reveal>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-ink/10 border border-brand-ink/10">
            {CASE_STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08} className="bg-brand-offwhite p-7">
                <div className="flex items-center justify-between mb-4">
                  <s.icon className="w-7 h-7 text-brand-orange" strokeWidth={1.5} />
                  <span className="font-mono text-sm text-brand-ink/20">0{i + 1}</span>
                </div>
                <h3 className="font-display font-semibold text-lg tracking-tight text-brand-ink leading-snug">{s.title}</h3>
                <p className="mt-2 text-brand-grey leading-relaxed text-sm">{s.body}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link to="/ayodhya" data-testid="approach-case-link" className="mt-10 inline-flex items-center gap-2 text-brand-ink font-medium hover:text-brand-orange transition-colors underline underline-offset-4">
              See the full Ayodhya case <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* How we work with investors */}
      <section className="bg-brand-paper py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">Working With Us</span>
            <h2 className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tighter text-brand-ink leading-[1.05] max-w-3xl">How we take your capital and grow it.</h2>
            <p className="mt-6 text-lg text-brand-grey leading-relaxed max-w-2xl">A simple, transparent path from first conversation to exit. You hold the land in your own name at every step.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {ENGAGE.map((e, i) => (
              <Reveal key={e.n} delay={i * 0.1}>
                <div className="h-full bg-brand-card border border-brand-ink/10 rounded-sm p-7 hover:border-brand-orange transition-colors duration-500">
                  <div className="flex items-center justify-between mb-5">
                    <e.icon className="w-8 h-8 text-brand-orange" strokeWidth={1.5} />
                    <span className="font-mono text-sm text-brand-ink/20">{e.n}</span>
                  </div>
                  <h3 className="font-display font-semibold text-xl tracking-tight text-brand-ink leading-snug">{e.title}</h3>
                  <p className="mt-2 text-brand-grey leading-relaxed text-[15px]">{e.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pull quotes + fees */}
      <section className="bg-brand-dark py-28 lg:py-36 relative overflow-hidden">
        <div className="grain" />
        <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10 space-y-16">
          <Reveal>
            <blockquote className="font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-brand-offwhite">
              "In a market built on <span className="text-brand-orange">asymmetry</span>, timing is the return."
            </blockquote>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border-t border-brand-offwhite/10 pt-12">
              <span className="text-brand-orange text-sm font-semibold tracking-[0.2em] uppercase">How we're paid</span>
              <p className="mt-5 font-display text-2xl sm:text-3xl tracking-tight leading-snug text-brand-offwhite">
                We do not charge on the way in. Our compensation is earned at exit, tied to investor profit.
                <span className="text-brand-offwhite/50"> If a transaction does not perform, neither do we.</span>
              </p>
              <button type="button" onClick={() => openBooking("default", "approach_modal")} data-testid="approach-book-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-orange px-7 py-4 font-semibold text-white hover:bg-brand-offwhite hover:text-brand-ink transition-colors">
                Book a 15-minute call <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      <FaqSection />
      <Footer />
      <WhatsAppFab />
    </main>
  );
}
